import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { HttpStatus, LoggerService } from "@nestjs/common";

import {
  IBaseRechargeQueryParams,
  IPageableRechargeResponse,
} from "./model/RechargeCommon";
import { ExecutionCounter } from "./util/ParallelExecutionCounter";

const REQUEST_TRY_COUNT = 3;
const RETRY_DEFAULT_WAIT_TIME_MS = 5_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

export abstract class RechargeResourceEndpoint<
  SINGLE,
  MULTI extends IPageableRechargeResponse,
  PARAMS extends IBaseRechargeQueryParams<string>,
  T,
> {

  protected constructor(
    protected readonly axiosInstance: AxiosInstance,
    protected readonly accessTokenGetter: () => string,
    protected readonly resourcePath: string,
    protected readonly logger: LoggerService,
    protected readonly waitTimeMs: number = RETRY_DEFAULT_WAIT_TIME_MS,
  ) {
    this.logger.log(`Initiated '${resourcePath}' Recharge Endpoint`, { resourcePath, waitTimeMs });
  }

  /** @param id The ID of the object to fetch
   * @param params The query params to add to the list URL,
   * used to filter the query and control the order of results
   * and/or include related objects
   * @return the object with the given ID or undefined if the object does not exist
   */
  public async get(id: number | bigint, params?: PARAMS extends { include?: string } ? Pick<PARAMS, "include"> : never): Promise<T | undefined> {
    try {
      const resp = await this.getWithRetry<SINGLE>(
        `${this.resourcePath}/${id}`,
        false,
        { headers: this.getHeaders(), params },
      );
      const responseData: SINGLE = resp.data;
      const responseObject: T = this.singleGetter(responseData);
      return responseObject;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const ae: AxiosError = e;
        if (ae.response?.status === HttpStatus.NOT_FOUND) {
          this.logger.warn(
            `${this.resourcePath} not found with id ${id}`,
            {
              resourcePath: this.resourcePath,
              id,
            },
          );
          return undefined;
        }
      }
      // Else, this is an actual error, so we just rethrow it
      throw e;
    }
  }

  /** Gets a single page
   * @param params Passed as query params in the get request to filter the query and control the order of results
   * @param accessToken If provided, will use this token instead of getting a new one
   */
  public async getSinglePageByQueryParam(
    params: PARAMS | IBaseRechargeQueryParams<never>,
    accessToken?: string,
  ): Promise<MULTI> {
    const resp = await this.getWithRetry<MULTI>(
      this.resourcePath,
      true,
      {
        headers: this.getHeaders(accessToken || this.accessTokenGetter()),
        params,
      },
      // If the access token is set, then do not get new key on retry
      !accessToken,
    );
    return resp.data;
  }

  /** Same as calling the transform getter with an identity transform
   * @see getAllByQueryParamAndTransform */
  public async getAllByQueryParam(
    params: PARAMS,
  ): Promise<T[]> {
    return this.getAllByQueryParamAndTransform(
      {
        params,
        transformer: (it) => it,
      },
    );
  }

  /** Get all values from the relevant ReCharge endpoint.
   * @param params The query params to add to the list URL,
   * used to filter the query and control the order of results
   * @param transformer A transformation to perform on the retrieved objects.
   * It can be useful for reducing memory usage.
   * You could even make it so that the values are made into "jobs" by transforming them into promises.
   * @param listFilter Can be used as an early filter for the returned values.
   * If not provided, allows all entries.
   * @param useSingleAccessToken If true, will use the same access token for all requests.
   * True by default.
   */
  public async getAllByQueryParamAndTransform<Y>(
    {
      params,
      transformer,
      listFilter = (_it) => true,
      useSingleAccessToken = true,
    }: {
      params: PARAMS;
      transformer: { (it: T): Y };
      listFilter?: { (it: T): boolean };
      useSingleAccessToken?: boolean;
    },
  ): Promise<Y[]> {
    const retList: Y[] = [];
    const accessToken = useSingleAccessToken ? this.accessTokenGetter() : undefined;
    let currParams: PARAMS | IBaseRechargeQueryParams<never> = params;
    let currBatch: T[];
    do {
      // Load a page
      const currResponseData = await this.getSinglePageByQueryParam(currParams, accessToken);
      currBatch = this.multiGetter(currResponseData);
      // Filter and transform the elements in this page before adding them to the aggregated list
      retList.push(
        ...(
          currBatch
            .filter(listFilter)
            .map(transformer)
        ),
      );
      // Prepare to load the next page
      currParams = {
        // The limit is still necessary. It determines how many items are going to be in the next page
        limit: currParams.limit ?? undefined,
        // This is what allows us to iterate through all the results
        // We don't need the rest of the parameters after the first GET;
        // the cursor "remembers" what parameters it was called with
        cursor: currResponseData.next_cursor ?? undefined,
      };

      // Keep looping and loading pages while there is a next cursor and the current page was not empty
    } while (currParams.cursor && currBatch.length);
    return retList;
  }

  /** Runs the action for each item.
   * Items are processed in batches, so only a single batch is in-memory at a time.
   * This function will not catch exceptions thrown in action.
   * That means they can be used to stop the function early.
   * If you don't want the function to stop early in case of exception, wrap the action in a try-catch block.
   * @param params The query to use to filter items, if necessary.
   * I suggest you order the results by ID or similar to ensure consistent process order.
   * @param action The action to run on each fetched item.
   * If not provided, acts as a count.
   * @param listFilter Can be used as an early filter for the returned values.
   * If not provided, everything will be passed to the actions/count
   * @param useSingleAccessToken If set to true, the same token will be used for all fetch requests
   * @param limit If set, at most this many items will be processed
   * @param parallelism how many actions may run in parallel (with Promise.all)
   * @return the number of items that were loaded and passed the filter
   * or the number of loaded items if no filter was set
   */
  public async forEachByQueryParam(
    {
      params,
      action,
      listFilter,
      useSingleAccessToken = true,
      limit,
      parallelism = 1,
    }: {
      params: PARAMS;
      action?: { (it: T): Promise<void> };
      listFilter?: { (it: T): boolean };
      useSingleAccessToken?: boolean;
      limit?: number;
      parallelism?: number;
    },
  ): Promise<number> {
    const accessToken = useSingleAccessToken ? this.accessTokenGetter() : undefined;
    let currParams: PARAMS | IBaseRechargeQueryParams<never> = params;
    let currBatch: T[];
    const processedCounter = new ExecutionCounter();
    do {
      // Load a page
      const currResponseData = await this.getSinglePageByQueryParam(currParams, accessToken);
      currBatch = this.multiGetter(currResponseData);

      if (action) {
        // Filter the elements in this page before processing them
        let toProcess: Promise<void>[] = [];
        for (let i = 0; i < currBatch.length && (limit === undefined || processedCounter.value < limit); i += 1) {
          const item = currBatch[i];
          if (!listFilter || listFilter(item)) {
            toProcess.push(processedCounter.andThenIncrement(action(item)));
            if (toProcess.length >= parallelism) {
              await Promise.all(toProcess);
              toProcess = [];
            }
          }
        }
        if (toProcess.length > 0) {
          await Promise.all(toProcess);
        }
      } else {
        // If we have no action to run, then just count the ones we found
        processedCounter.increment(
          listFilter
            ? currBatch.filter(listFilter).length
            : currBatch.length,
        );
      }

      // Prepare to load the next page
      currParams = {
        // The limit is still necessary. It determines how many items are going to be in the next page
        limit: currParams.limit ?? undefined,
        // This is what allows us to iterate through all the results
        // We don't need the rest of the parameters after the first GET;
        // the cursor "remembers" what parameters it was called with
        cursor: currResponseData.next_cursor ?? undefined,
      };

      // Keep looping and loading pages while there is a next cursor and the current page was not empty
    } while (currParams.cursor && currBatch.length && (limit === undefined || processedCounter.value < limit));
    return processedCounter.value;
  }

  /** Runs the action for each batch.
   * Only one batch is in-memory at a time.
   * This function will not catch exceptions thrown in action.
   * That means they can be used to stop the function early.
   * If you don't want the function to stop early in case of exception, wrap the action in a try-catch block.
   * @param params The query to use to filter items, if necessary.
   * I suggest you order the results by ID or similar to ensure consistent process order.
   * @param action The action to run on the batch of fetched items.
   * @param useSingleAccessToken If set to true, the same token will be used for all fetch requests
   * @param numberToProcess If set, at most this many items will be processed
   * @return the number of items that were loaded
   */
  public async forBatchByQueryParam(
    {
      params,
      action,
      useSingleAccessToken = true,
      numberToProcess,
    }: {
      params: PARAMS;
      action: { (it: T[]): Promise<void> };
      useSingleAccessToken?: boolean;
      numberToProcess?: number;
    },
  ): Promise<number> {
    const accessToken = useSingleAccessToken ? this.accessTokenGetter() : undefined;
    let currParams: PARAMS | IBaseRechargeQueryParams<never> | undefined = params;
    let currBatch: T[] | undefined;
    const loadedCounter = new ExecutionCounter();
    do {
      // Load a page if we need to
      currParams = currParams && (numberToProcess === undefined || loadedCounter.value < numberToProcess)
        ? {
          ...currParams,
          // Ensure that we don't fetch more orders than necessary
          limit: numberToProcess
            ? Math.min(params.limit ?? 50, numberToProcess - loadedCounter.value)
            : params.limit,
        }
        : undefined;

      // If we should load the next page, load it.
      // If there's orders loaded, process them.
      // Doing both at the same time means we're preloading the next page as
      // we're waiting for the current page to finish processing, which results in a significant speedup.
      const result: [
        PromiseSettledResult<MULTI | undefined>,
        PromiseSettledResult<void | undefined>,
      ] = await Promise.allSettled([
        currParams ? this.getSinglePageByQueryParam(currParams, accessToken) : undefined,
        currBatch ? action(currBatch) : undefined,
      ]);
      const [ loadResult, processResult ] = result;

      // Handle errors (process errors take priority over load errors)
      if (processResult.status === "rejected") {
        const cause = processResult.reason;
        const message = "Failed while processing batch";
        this.logger.error(message, {
          cause,
          nextPageParams: currParams,
          loadedCounter,
        });
        throw new Error(message, { cause });
      }
      if (loadResult.status === "rejected") {
        const cause = loadResult.reason;
        const message = "Failed while loading next batch";
        this.logger.error(message, {
          cause,
          nextPageParams: currParams,
          loadedCounter,
        });
        throw new Error(message, { cause });
      }

      // If we were loading a page in this loop
      const currResponseData = loadResult.value;
      if (currResponseData) {
        // Prepare the loaded batch for processing
        currBatch = this.multiGetter(currResponseData);
        loadedCounter.increment(currBatch.length);
      } else {
        currBatch = undefined;
      }

      // Prepare to load the next page if there is one
      currParams = currParams && currResponseData && currResponseData.next_cursor ? {
        // The limit is still necessary. It determines how many items are going to be in the next page
        limit: currParams.limit ?? undefined,
        // This is what allows us to iterate through all the results
        // We don't need the rest of the parameters after the first GET;
        // the cursor "remembers" what parameters it was called with
        cursor: currResponseData.next_cursor,
      } : undefined;

      // Keep looping and loading pages while there is a next cursor or the current page was not empty
    } while (currParams || currBatch);
    return loadedCounter.value;
  }

  /** @param accessToken The access token to use.
   * If not provided, will use the accessTokenGetter to get one.
   * @return AccessToken and Version headers for ReCharge.
   * @protected
   */
  protected getHeaders(accessToken: string = this.accessTokenGetter()): {
    "X-Recharge-Access-Token": string;
    "X-Recharge-Version": "2021-11";
  } {
    return {
      "X-Recharge-Access-Token": accessToken,
      "X-Recharge-Version": "2021-11",
    };
  }

  /** @see requestWithRetry */
  protected async getWithRetry<RT, R extends AxiosResponse = AxiosResponse<RT>>(
    url: string,
    truncateLongBody: boolean,
    config: AxiosRequestConfig<never> = {},
    newKeyOnRetry = true,
  ): Promise<R> {
    return this.requestWithRetry(
      {
        url,
        config,
        newKeyOnRetry,
        // This is just a get request, so it should be fine to retry
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody,
      },
    );
  }

  /** Will always retry responses with response code
   * {@link HttpStatus.TOO_MANY_REQUESTS} up to {@link REQUEST_TRY_COUNT} times.
   * @param url The url path to which the request should be made (e.g. "/charges/123/process")
   * MUST always start with this.resourcePath.
   * @param config Config for the request.
   * You'll probably want to set the method field and the data field if this is a method that accepts a body.
   * If you set the X-Recharge-Access-Token header, the given api token will be used instead of getting a new one.
   * @param newKeyOnRetry If set to true, every time the request is retried, a new key will be used,
   * else the same as the one used in the first request will be used.
   * @param retryGatewayTimeout If set to true, response codes of status {@link HttpStatus.GATEWAY_TIMEOUT}
   * will also be retried.
   * False by default.
   * You should probably only set this to true when you're fine with the request happening more than once.
   * That usually means that either the request has no effect or the HTTP request is idempotent.
   * @param retryConflict If set to true, response codes of status {@link HttpStatus.CONFLICT} will also be retried.
   * False by default.
   * You should probably only set this to true when you're fine with the request happening more than once.
   * That usually means that either the request has no
   * effect or the HTTP request is idempotent.
   * @param truncateLongBody If set to true, then if the body of the request exceeds
   * a certain length it will be truncated when logging.
   * @protected
   * @template D The body.
   * Set to never if this is a method with no response (e.g. GET).
   * @template RT The return type.
   * Note that we don't verify that the type matches the returned data, so it's up to you to test and make sure.
   * @return The response parsed as JSON
   */
  protected async requestWithRetry<RT, D = never, R extends AxiosResponse = AxiosResponse<RT, D>>(
    {
      url,
      config,
      newKeyOnRetry,
      retryGatewayTimeout = false,
      retryConflict = false,
      truncateLongBody,
    }: {
      url: string;
      config: AxiosRequestConfig<D>;
      newKeyOnRetry: boolean;
      retryGatewayTimeout?: boolean;
      retryConflict?: boolean;
      truncateLongBody: boolean;
    },
  ): Promise<R> {
    if (!url.startsWith(this.resourcePath)) {
      throw new Error(`URL ${url} did not start with expected resourcePath ${this.resourcePath}`);
    }

    let triesLeft = REQUEST_TRY_COUNT;
    const oldHeaders = config.headers ?? {};
    let currConfig: AxiosRequestConfig<D> = {
      ...config,
      url,
      headers: {
        ...this.getHeaders(oldHeaders["X-Recharge-Access-Token"]),
        ...oldHeaders,
      },
    };

    // While we have more tries
    do {
      triesLeft -= 1;
      try {
        this.logger.debug?.(
          `Starting request on ${url}`,
          {
            url,
            currConfig,
            newKeyOnRetry,
            retryGatewayTimeout,
            retryConflict,
          },
        );

        // Do the request
        const resp = await this.axiosInstance.request<RT, R, D>(
          currConfig,
        );
        this.logger.debug?.(
          `Got response from ${url}`,
          {
            url,
            currConfig,
            newKeyOnRetry,
            retryGatewayTimeout,
            retryConflict,
            ...this.getResponseLogData(resp, truncateLongBody),
          },
        );
        return resp;
      } catch (e) {
        // If the request failed
        if (axios.isAxiosError(e)) {
          const ae: AxiosError = e;

          // And there are retries left
          if (triesLeft > 0 && (
            // And the failure was retryable per the config
            ae.response?.status === HttpStatus.TOO_MANY_REQUESTS
            || (retryGatewayTimeout && (ae.code === "ECONNRESET" || ae.code === "EHOSTUNREACH" || ae.response?.status === HttpStatus.GATEWAY_TIMEOUT))
            || (retryConflict && ae.response?.status === HttpStatus.CONFLICT)
          )) {
            this.logger.warn(
              `Request failed, will retry request on ${url}`,
              {
                url,
                currConfig,
                newKeyOnRetry,
                retryGatewayTimeout,
                retryConflict,
                triesLeft,
                ...this.getResponseLogData(ae.response, false),
              },
            );

            // Wait a few seconds.
            //   This gives us some time for some more access tokens to be generated
            //   and/or for whatever conflicting operation is going on to finish
            await sleep(this.waitTimeMs);
            if (newKeyOnRetry) {
              const newKey = this.accessTokenGetter();
              currConfig = {
                ...currConfig,
                headers: {
                  ...currConfig.headers,
                  "X-Recharge-Access-Token": newKey,
                },
              };
            }
            continue;
          }

          // Else, if there are no retries left or if this isn't a retryable result code
          this.logger.error(
            `ReCharge Request failed on ${url}`,
            {
              url,
              currConfig,
              newKeyOnRetry,
              retryGatewayTimeout,
              retryConflict,
              errorCode: ae.code,
              ...this.getResponseLogData(ae.response, false),
            },
          );
        } else {
          this.logger.error(
            `Unhandled exception while processing request on ${url}`,
            {
              url,
              currConfig,
              newKeyOnRetry,
              retryGatewayTimeout,
              retryConflict,
              cause: e,
            },
          );
        }

        // Else, this is an actual error, so just rethrow it
        throw e;
      }
    } while (triesLeft > 0);

    // We always handle failures in the loop by throwing an exception, so we should never get here
    throw new Error(`${url} reached the end of retry loop, should never happen`);
  }

  protected getResponseLogData(
    response: AxiosResponse | null | undefined,
    truncateLongBody: boolean,
  ): undefined | {
      relevantHeaders: {
        "content-type": AxiosHeaderValue;
        date: AxiosHeaderValue;
        "retry-after": AxiosHeaderValue;
        "x-recharge-limit": AxiosHeaderValue;
        "x-recharge-version": AxiosHeaderValue;
        "x-request-id": AxiosHeaderValue;
      };
      responseBody: unknown;
      responseCode: number;
    } {
    return response ? {
      responseCode: response.status,
      responseBody: truncateLongBody
        ? truncateLongBodyIfNecessary(response.data)
        : response.data,
      relevantHeaders: {
        "x-recharge-version": response.headers["x-recharge-version"],
        "x-recharge-limit": response.headers["x-recharge-limit"],
        "x-request-id": response.headers["x-request-id"],
        "content-type": response.headers["content-type"],
        "retry-after": response.headers["retry-after"],
        // eslint-disable-next-line @typescript-eslint/dot-notation
        date: response.headers["date"],
      },
    } : undefined;
  }

  /** This gets a wrapped result from the ReCharge API and unwraps it */
  protected abstract singleGetter(t: SINGLE): T;

  /** This gets a wrapped result from the ReCharge API and unwraps it */
  protected abstract multiGetter(t: MULTI): T[];

}

const MAX_CONTENT_LENGTH = 500 as const;

function truncateLongBodyIfNecessary(
  toCheck: unknown,
  maxContentLength: number = MAX_CONTENT_LENGTH,
): unknown | string {
  const asString = JSON.stringify(toCheck);
  if (asString.length > maxContentLength) {
    return `${asString.slice(0, maxContentLength)}...TRUNCATED...`;
  }
  return toCheck;
}
